<template>
    <div class="commissions-forecast-component">
        <view-header :title="'Commissions Forecast'">
            <template v-slot:viewicon>
                <i class="fa-duotone fa-solid fa-calendars"></i>
            </template>
            <template v-slot:viewfilters>
                <span class="k-textbox k-grid-search k-display-flex">
                    <k-input :style="{ width: '300px' }"
                                placeholder="Search by player name..."
                                :value="searchWord"
                                :icon-name="'search'"
                                :show-clear-button="true"
                                @input="onSearchChange"
                                @change="onSearchChange"></k-input>
                </span>
                <k-button class="edit-btn edit-all-btn" v-if="!editAllMode" @click="onEditAllRows"><i class="fa-solid fa-pen-to-square"></i> Edit All</k-button>
                <k-button class="edit-btn save-all-btn" :disabled="saveAllBtnDisabled" v-if="editAllMode"  @click="onSaveAll"><i class="fa-solid fa-floppy-disk"></i> Save Changes</k-button>
                <k-button class="edit-btn cancel-edit-all-btn" :disabled="!editAllMode" @click="onCancelEditAll"><i class="fa-solid fa-rotate-left"></i> Cancel</k-button>
                <k-button class="edit-btn add-player-btn" @click="onAddRow"><i class="fa-solid fa-plus"></i> Add Player</k-button>
                <k-button class="edit-btn reorder-btn" :disabled="filteredForecastGridData.some(data => data.inEdit)" @click="onRowReorder"><i class="fa-solid fa-arrow-down-arrow-up"></i> Player Order</k-button>
                <k-button class="edit-btn export-btn" :disabled="editAllMode" @click="exportExcel"><i class="fa-solid fa-file-export"></i> Export to Excel</k-button>
            </template>
        </view-header>
        <div class="forecast-grid-container">
            <div class="summary-grid-container">
                <k-grid :data-items="summaryData"
                        :data-item-key="'season'"
                        :columns="summaryColumns"
                        :row-render="getRowStyle"
                        ref="summarygrid">
                        <template v-slot:seasonColumn="{ props }">
                            <td :class="['k-table-td', 'summary-season', { 'projected': props.dataItem.season === 'Projected' }]">{{ props.dataItem.season }}</td>
                        </template>
                </k-grid>
            </div>
            <div class="player-grid-container">
                <k-grid :data-items="filteredForecastGridData"
                class="player-grid"
                    :data-item-key="'dataId'"
                    :columns="filteredForecastColumns"
                    :edit-field="'inEdit'"
                    :pageable="gridPageable"
                    :take="take"
                    :total="total"
                    :skip="skip"
                    @datastatechange="dataStateChange"
                    @itemchange="onItemChange"
                    ref="forecastgrid">
                    <template v-slot:actionCell="{ props }">
                        <td v-if="!props.dataItem['inEdit']" class="k-command-cell k-table-td">
                            <k-button class="row-btn row-edit-btn" @click="onEditRow(props.dataItem)">Edit</k-button>
                            <k-button class="row-btn row-remove-btn" @click="onDeleteRow(props.dataItem)">Delete</k-button>
                            <k-button class="player-note-btn" @click="onUpsertContractNote(props.dataItem)" title="Add Contract Notes"><i class="fa-regular fa-pen"></i></k-button>
                        </td>
                        <td v-else class="k-command-cell k-table-td">
                            <k-button class="row-btn row-update-btn" :disabled="getUpdateBtnStatus(props.dataItem)" @click="onUpdateRow(props.dataItem)">Save</k-button>
                            <k-button class="row-btn row-cancel-btn" @click="onCancelEdit(props.dataItem)">Cancel</k-button>
                            <k-button class="player-note-btn" disabled @click="onUpsertContractNote(props.dataItem)" title="Add Contract Notes"><i class="fa-regular fa-pen"></i></k-button>
                        </td>
                    </template>
                    <template v-slot:nameCell="{ props }">
                        <td :class="['k-table-td', 'display-name', { 'edit-all-mode': editAllMode }, { 'edit-mode': props.dataItem.inEdit }]">{{ props.dataItem.displayName }} 
                            <div class="tooltip-container" v-if="props.dataItem.contractNotes">
                                <i :id="props.dataItem.playerId" class="fa-solid fa-circle-info tooltip" @mouseover="props.dataItem.showTooltip = true" @mouseleave="props.dataItem.showTooltip = false"></i>
                                <div v-bind:class="['contract__tooltip', { 'tooltip--show': props.dataItem.showTooltip }]">
                                    <p class="tooltip--text">{{ props.dataItem.contractNotes }}</p>
                                </div>
                            </div>
                        </td>
                    </template>
                    <template v-slot:year1Projected="{ props }">
                        <td class="k-table-td projected-first" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year1Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td projected-first" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year1Projected" /></td>
                    </template>
                    <template v-slot:year1Salary="{ props }">
                        <td :class="['k-table-td', 'salary-first', { 'projected': props.dataItem.year1Projected }, { 'edit-mode': filteredForecastGridData.some(data => data.inEdit)} ]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year1Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year1Projected }]" @change="onSalaryUpdated(props.dataItem, 1)" v-model="props.dataItem.year1Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year1Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year1Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year1Receivable) }}</td>
                    </template>
                    <template v-slot:year1CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year1Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year1CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year1Projected }]" @change="onCompPercentageUpdated(props.dataItem, 1)" v-model="props.dataItem.year1CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year2Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year2Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year2Projected" /></td>
                    </template>
                    <template v-slot:year2Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year2Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year2Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year2Projected }]" @change="onSalaryUpdated(props.dataItem, 2)" v-model="props.dataItem.year2Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year2Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year2Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year2Receivable) }}</td>
                    </template>
                    <template v-slot:year2CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year2Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year2CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year2Projected }]" @change="onCompPercentageUpdated(props.dataItem, 2)" v-model="props.dataItem.year2CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year3Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year3Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year3Projected" /></td>
                    </template>
                    <template v-slot:year3Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year3Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year3Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year3Projected }]" @change="onSalaryUpdated(props.dataItem, 3)" v-model="props.dataItem.year3Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year3Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year3Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year3Receivable) }}</td>
                    </template>
                    <template v-slot:year3CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year3Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year3CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year3Projected }]" @change="onCompPercentageUpdated(props.dataItem, 3)" v-model="props.dataItem.year3CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year4Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year4Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year4Projected" /></td>
                    </template>
                    <template v-slot:year4Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year4Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year4Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year4Projected }]" @change="onSalaryUpdated(props.dataItem, 4)" v-model="props.dataItem.year4Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year4Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year4Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year4Receivable) }}</td>
                    </template>
                    <template v-slot:year4CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year4Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year4CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year4Projected }]" @change="onCompPercentageUpdated(props.dataItem, 4)" v-model="props.dataItem.year4CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year5Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year5Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year5Projected" /></td>
                    </template>
                    <template v-slot:year5Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year5Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year5Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year5Projected }]" @change="onSalaryUpdated(props.dataItem, 5)" v-model="props.dataItem.year5Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year5Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year5Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year5Receivable) }}</td>
                    </template>
                    <template v-slot:year5CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year5Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year5CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year5Projected }]" @change="onCompPercentageUpdated(props.dataItem, 5)" v-model="props.dataItem.year5CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year6Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year6Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year6Projected" /></td>
                    </template>
                    <template v-slot:year6Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year6Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year6Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year6Projected }]" @change="onSalaryUpdated(props.dataItem, 6)" v-model="props.dataItem.year6Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year6Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year6Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year6Receivable) }}</td>
                    </template>
                    <template v-slot:year6CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year6Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year6CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year6Projected }]" @change="onCompPercentageUpdated(props.dataItem, 6)" v-model="props.dataItem.year6CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year7Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year7Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year7Projected" /></td>
                    </template>
                    <template v-slot:year7Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year7Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year7Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year7Projected }]" @change="onSalaryUpdated(props.dataItem, 7)" v-model="props.dataItem.year7Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year7Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year7Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year7Receivable) }}</td>
                    </template>
                    <template v-slot:year7CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year7Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year7CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year7Projected }]" @change="onCompPercentageUpdated(props.dataItem, 7)" v-model="props.dataItem.year7CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year8Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year8Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year8Projected" /></td>
                    </template>
                    <template v-slot:year8Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year8Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year8Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year8Projected }]" @change="onSalaryUpdated(props.dataItem, 8)" v-model="props.dataItem.year8Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year8Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year8Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year8Receivable) }}</td>
                    </template>
                    <template v-slot:year8CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year8Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year8CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year8Projected }]" @change="onCompPercentageUpdated(props.dataItem, 8)" v-model="props.dataItem.year8CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year9Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year9Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year9Projected" /></td>
                    </template>
                    <template v-slot:year9Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year9Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year9Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year9Projected }]" @change="onSalaryUpdated(props.dataItem, 9)" v-model="props.dataItem.year9Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year9Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year9Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year9Receivable) }}</td>
                    </template>
                    <template v-slot:year9CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year9Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year9CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year9Projected }]" @change="onCompPercentageUpdated(props.dataItem, 9)" v-model="props.dataItem.year9CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year10Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year10Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year10Projected" /></td>
                    </template>
                    <template v-slot:year10Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year10Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year10Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year10Projected }]" @change="onSalaryUpdated(props.dataItem, 10)" v-model="props.dataItem.year10Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year10Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year10Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year10Receivable) }}</td>
                    </template>
                    <template v-slot:year10CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year10Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year10CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year10Projected }]" @change="onCompPercentageUpdated(props.dataItem, 10)" v-model="props.dataItem.year10CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                    <template v-slot:year11Projected="{ props }">
                        <td class="k-table-td" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year11Projected ? 'Y' : 'N' }}</td>
                        <td class="k-table-td" v-else><k-checkbox @change="onProjectedUpdated(props.dataItem)" v-model="props.dataItem.year11Projected" /></td>
                    </template>
                    <template v-slot:year11Salary="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year11Projected }]" v-if="!props.dataItem['inEdit']">{{ $options.filters.formatCurrency(props.dataItem.year11Salary) }}</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year11Projected }]" @change="onSalaryUpdated(props.dataItem, 11)" v-model="props.dataItem.year11Salary" :format="'c0'" :min="0" /></td>
                    </template>
                    <template v-slot:year11Receivable="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year11Projected }]">{{ $options.filters.formatCurrency(props.dataItem.year11Receivable) }}</td>
                    </template>
                    <template v-slot:year11CompPercentage="{ props }">
                        <td :class="['k-table-td', { 'projected': props.dataItem.year11Projected }]" v-if="!props.dataItem['inEdit']">{{ props.dataItem.year11CompPercentage.toFixed(1) }}%</td>
                        <td class="k-table-td" v-else><k-numeric-input :class="[{ 'projected': props.dataItem.year11Projected }]" @change="onCompPercentageUpdated(props.dataItem, 11)" v-model="props.dataItem.year11CompPercentage" :format="'n1'" :min="0" /></td>
                    </template>
                </k-grid>
            </div>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showAddRowDialog" :title="'Add Player'" class="default__dialog add-row-dialog" @close="toggleAddRowDialog">
                <k-dropdown :data-items="addRowSelects"
                            v-model="selectedAddRowPlayer"
                            :text-field="'fullName'"
                            :data-item-key="'id'"
                            :filterable="true"
                            @change="onAddRowPlayerSelected"
                            @filterchange="onAddRowPlayerFilterChange"></k-dropdown>
                <k-action-bar>
                    <k-button :disabled="!selectedAddRowPlayer.id" class="add-player-btn" @click="onSaveAddRowPlayer">Add Player</k-button>
                    <k-button @click="toggleAddRowDialog">Cancel</k-button>
                </k-action-bar>
            </k-dialog>
        </transition>

        <div class="loader-container" v-if="isLoading">
            <k-loader :size="'large'" :type="'infinite-spinner'"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showContractNotesDialog" 
                class="default__dialog contract-notes-dialog"
                :title="selectedNotesRow.displayName + ': Contract Notes'" 
                @close="toggleContractNotesDialog">
                <label>
                    Player Contract Note
                    <k-input v-model="selectedNotes" />
                </label>
                <k-action-bar>
                    <k-button class="save-note-btn" @click="onSaveContractNote">Save</k-button>
                    <k-button @click="toggleContractNotesDialog">Cancel</k-button>
                </k-action-bar>
            </k-dialog>
        </transition>

        <commissions-forecast-reorder-dialog :show-dialog="showReorderDialog"
                                             :season-strings="seasonStrings"
                                             @dialogclosed="toggleReorderDialog"
                                             @playerorderupdated="onPlayerOrderUpdated"></commissions-forecast-reorder-dialog>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>

        <k-notification-group class="default-notification-container">
                <k-fade :appear="successNotification">
                    <k-notification v-if="successNotification"
                                    :type="{ style: 'success', icon: true }"
                                    :closable="true"
                                    @close="onCloseNotification('successNotification')">
                        <span>{{ notificationMsg }}</span>
                    </k-notification>
                </k-fade>
        </k-notification-group>
    </div>
</template>

<script>
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Grid } from '@progress/kendo-vue-grid';
    import { Checkbox, NumericTextBox, Input } from "@progress/kendo-vue-inputs";
    import { Button } from '@progress/kendo-vue-buttons';
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade } from '@progress/kendo-vue-animation';
    import { DropDownList } from '@progress/kendo-vue-dropdowns';

    import { workbookOptions, toDataURL } from '@progress/kendo-vue-excel-export';
    import { process, filterBy } from '@progress/kendo-data-query';
    import { saveAs } from '@progress/kendo-file-saver';

    import commissionsForecastReorderDialog from "./commissionsForecastReorderDialog.vue";

    import viewHeader from '../components/viewHeader.vue';

    import isEqual from 'lodash.isequal';
    import cloneDeep from 'lodash.clonedeep';
    import omit from 'lodash.omit';

    import auth from '../authConfig';
    import apiService from '../api/api.service.js';

    export default {
        name: 'commissionsForecastComponent',
        emits: [],
        data() {
            return {
                addRowPlayers: [],
                addRowSelects: [],
                editAllMode: false,
                errorMsg: '',
                errorTitle: '',
                filteredForecastGridData: [],
                forecastColumnsDefault: [
                    { cell: 'actionCell', width: '175px', locked: true },
                    { cell: 'nameCell', title: 'Player', width: '150px', locked: true, editable: false },
                ],
                forecastColumnsEditAll: [
                    { cell: 'nameCell', title: 'Player', width: '325px', locked: true, editable: false },
                ],
                forecastData: [],
                forecastGridData: [],
                gridPageable: {
                    buttonCount: 5,
                    info: true,
                    type: "numeric",
                    pageSizes: [5, 10, 25, 50],
                    previousNext: true,
                },
                isLoading: false,
                notificationMsg: '',
                searchWord: '',
                seasonKeys: ['year1SeasonString', 'year2SeasonString', 'year3SeasonString', 'year4SeasonString', 'year5SeasonString', 'year6SeasonString',
                    'year7SeasonString', 'year8SeasonString', 'year9SeasonString', 'year10SeasonString', 'year11SeasonString'
                ],
                selectedAddRowPlayer: { id: null, fullName: 'Select Player'},
                selectedNotes: null,
                selectedNotesRow: null,
                showAddRowDialog: false,
                showContractNotesDialog: false,
                showErrorDialog: false,
                showReorderDialog: false,
                skip: 0,
                successNotification: false,
                summaryColumns: [
                    { cell: 'seasonColumn', field: 'season', title: 'Season', width: '325px', locked: true }
                ],
                summaryData: [
                    { 
                        season: 'Guaranteed', year1Salary: 0, year1Commission: 0, year1CompPercentage: 0 , year2Salary: 0, year2Commission: 0, year2CompPercentage: 0,
                        year3Salary: 0, year3Commission: 0, year3CompPercentage: 0, year4Salary: 0, year4Commission: 0, year4CompPercentage: 0,
                        year5Salary: 0, year5Commission: 0, year5CompPercentage: 0, year6Salary: 0, year6Commission: 0, year6CompPercentage: 0,
                        year7Salary: 0, year7Commission: 0, year7CompPercentage: 0, year8Salary: 0, year8Commission: 0, year8CompPercentage: 0,
                        year9Salary: 0, year9Commission: 0, year9CompPercentage: 0, year10Salary: 0, year10Commission: 0, year10CompPercentage: 0,
                        year11Salary: 0, year11Commission: 0, year11CompPercentage: 0 
                    },
                    { 
                        season: 'Projected', year1Salary: 0, year1Commission: 0, year1CompPercentage: 0 , year2Salary: 0, year2Commission: 0, year2CompPercentage: 0,
                        year3Salary: 0, year3Commission: 0, year3CompPercentage: 0, year4Salary: 0, year4Commission: 0, year4CompPercentage: 0,
                        year5Salary: 0, year5Commission: 0, year5CompPercentage: 0, year6Salary: 0, year6Commission: 0, year6CompPercentage: 0,
                        year7Salary: 0, year7Commission: 0, year7CompPercentage: 0, year8Salary: 0, year8Commission: 0, year8CompPercentage: 0,
                        year9Salary: 0, year9Commission: 0, year9CompPercentage: 0, year10Salary: 0, year10Commission: 0, year10CompPercentage: 0,
                        year11Salary: 0, year11Commission: 0, year11CompPercentage: 0 
                    },
                    { 
                        season: 'Guaranteed + Projected', year1Salary: 0, year1Commission: 0, year1CompPercentage: 0 , year2Salary: 0, year2Commission: 0, year2CompPercentage: 0,
                        year3Salary: 0, year3Commission: 0, year3CompPercentage: 0, year4Salary: 0, year4Commission: 0, year4CompPercentage: 0,
                        year5Salary: 0, year5Commission: 0, year5CompPercentage: 0, year6Salary: 0, year6Commission: 0, year6CompPercentage: 0,
                        year7Salary: 0, year7Commission: 0, year7CompPercentage: 0, year8Salary: 0, year8Commission: 0, year8CompPercentage: 0,
                        year9Salary: 0, year9Commission: 0, year9CompPercentage: 0, year10Salary: 0, year10Commission: 0, year10CompPercentage: 0,
                        year11Salary: 0, year11Commission: 0, year11CompPercentage: 0 
                    },
                ],
                take: 25,
                total: 0
            }
        },
        computed: {
            filteredForecastColumns() {
                if (this.editAllMode) {
                    return [...this.forecastColumnsEditAll, ...this.forecastColumns];
                } else {
                    return [...this.forecastColumnsDefault, ...this.forecastColumns]
                }
            },
            forecastColumns() {
                if (this.editAllMode || this.filteredForecastGridData.some(data => data.inEdit)) {
                    return [
                        { cell: 'year1Projected', field: 'year1Projected', title: 'Proj', width: '60px' },
                        { cell: 'year1Salary', field: 'year1Salary', title: 'Salary', width: '150px' },
                        { cell: 'year1Receivable', field: 'year1Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year1CompPercentage', field: 'year1CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year2Projected', field: 'year2Projected', title: 'Proj', width: '60px' },
                        { cell: 'year2Salary', field: 'year2Salary', title: 'Salary', width: '150px' },
                        { cell: 'year2Receivable', field: 'year2Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year2CompPercentage', field: 'year2CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year3Projected', field: 'year3Projected', title: 'Proj', width: '60px' },
                        { cell: 'year3Salary', field: 'year3Salary', title: 'Salary', width: '150px' },
                        { cell: 'year3Receivable', field: 'year3Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year3CompPercentage', field: 'year3CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year4Projected', field: 'year4Projected', title: 'Proj', width: '60px' },
                        { cell: 'year4Salary', field: 'year4Salary', title: 'Salary', width: '150px' },
                        { cell: 'year4Receivable', field: 'year4Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year4CompPercentage', field: 'year4CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year5Projected', field: 'year5Projected', title: 'Proj', width: '60px' },
                        { cell: 'year5Salary', field: 'year5Salary', title: 'Salary', width: '150px' },
                        { cell: 'year5Receivable', field: 'year5Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year5CompPercentage', field: 'year5CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year6Projected', field: 'year6Projected', title: 'Proj', width: '60px' },
                        { cell: 'year6Salary', field: 'year6Salary', title: 'Salary', width: '150px' },
                        { cell: 'year6Receivable', field: 'year6Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year6CompPercentage', field: 'year6CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year7Projected', field: 'year7Projected', title: 'Proj', width: '60px' },
                        { cell: 'year7Salary', field: 'year7Salary', title: 'Salary', width: '150px' },
                        { cell: 'year7Receivable', field: 'year7Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year7CompPercentage', field: 'year7CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year8Projected', field: 'year8Projected', title: 'Proj', width: '60px' },
                        { cell: 'year8Salary', field: 'year8Salary', title: 'Salary', width: '150px' },
                        { cell: 'year8Receivable', field: 'year8Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year8CompPercentage', field: 'year8CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year9Projected', field: 'year9Projected', title: 'Proj', width: '60px' },
                        { cell: 'year9Salary', field: 'year9Salary', title: 'Salary', width: '150px' },
                        { cell: 'year9Receivable', field: 'year9Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year9CompPercentage', field: 'year9CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year10Projected', field: 'year10Projected', title: 'Proj', width: '60px' },
                        { cell: 'year10Salary', field: 'year10Salary', title: 'Salary', width: '150px' },
                        { cell: 'year10Receivable', field: 'year10Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year10CompPercentage', field: 'year10CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year11Projected', field: 'year11Projected', title: 'Proj', width: '60px' },
                        { cell: 'year11Salary', field: 'year11Salary', title: 'Salary', width: '150px' },
                        { cell: 'year11Receivable', field: 'year11Receivable', title: 'Receivable', editable: false, width: '150px'},
                        { cell: 'year11CompPercentage', field: 'year11CompPercentage', title: 'Comp %', width: '100px'}
                    ]
                } else {
                    return [
                        { cell: 'year1Salary', field: 'year1Salary', title: 'Salary', width: '180px' },
                        { cell: 'year1Receivable', field: 'year1Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year1CompPercentage', field: 'year1CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year2Salary', field: 'year2Salary', title: 'Salary', width: '180px' },
                        { cell: 'year2Receivable', field: 'year2Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year2CompPercentage', field: 'year2CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year3Salary', field: 'year3Salary', title: 'Salary', width: '180px' },
                        { cell: 'year3Receivable', field: 'year3Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year3CompPercentage', field: 'year3CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year4Salary', field: 'year4Salary', title: 'Salary', width: '180px' },
                        { cell: 'year4Receivable', field: 'year4Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year4CompPercentage', field: 'year4CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year5Salary', field: 'year5Salary', title: 'Salary', width: '180px' },
                        { cell: 'year5Receivable', field: 'year5Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year5CompPercentage', field: 'year5CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year6Salary', field: 'year6Salary', title: 'Salary', width: '180px' },
                        { cell: 'year6Receivable', field: 'year6Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year6CompPercentage', field: 'year6CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year7Salary', field: 'year7Salary', title: 'Salary', width: '180px' },
                        { cell: 'year7Receivable', field: 'year7Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year7CompPercentage', field: 'year7CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year8Salary', field: 'year8Salary', title: 'Salary', width: '180px' },
                        { cell: 'year8Receivable', field: 'year8Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year8CompPercentage', field: 'year8CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year9Salary', field: 'year9Salary', title: 'Salary', width: '180px' },
                        { cell: 'year9Receivable', field: 'year9Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year9CompPercentage', field: 'year9CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year10Salary', field: 'year10Salary', title: 'Salary', width: '180px' },
                        { cell: 'year10Receivable', field: 'year10Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year10CompPercentage', field: 'year10CompPercentage', title: 'Comp %', width: '100px'},
                        { cell: 'year11Salary', field: 'year11Salary', title: 'Salary', width: '180px' },
                        { cell: 'year11Receivable', field: 'year11Receivable', title: 'Receivable', editable: false, width: '180px'},
                        { cell: 'year11CompPercentage', field: 'year11CompPercentage', title: 'Comp %', width: '100px'}
                    ]
                }
            },
            saveAllBtnDisabled() {
                this.forecastData.forEach((data, i) => {
                    for (const key in data) {
                        if (this.forecastGridData[i][key] != data[key]) {
                            console.log(key, this.forecastGridData[i][key], data[key]);
                        }
                    }
                })

                return isEqual(this.forecastData.map(data => omit(data, ['inEdit'])), this.forecastGridData.map(data => omit(data, ['inEdit'])));
            },
            seasonStrings() {
                if (this.forecastData.length === 0) {
                    return [];
                }

                return this.seasonKeys.map(key  => this.forecastData[0][key]);
            }
        },
        mounted() {
            if (this.forecastData.length === 0) {
                this.getForecastItems();
            }

            let summaryGrid = this.$refs.summarygrid.$el.querySelector('.k-grid-content');
            let forecastGrid = this.$refs.forecastgrid.$el.querySelector('.k-grid-content');

            forecastGrid.addEventListener('scroll', () => {
                summaryGrid.scrollLeft = forecastGrid.scrollLeft;
            });
        },
        methods: {
            assignAddRowSelects(players) {
                this.addRowPlayers = players;

                this.addRowSelects = cloneDeep(this.addRowPlayers);
                this.addRowSelects.unshift({
                    fullName: 'Select Player',
                    id: null
                });
            },
            assignForecastData(data) {
                // let ansons = data.filter(data => data.displayName === 'R.Ansons' || data.displayName === 'G.Ahn');
                // console.log('ansons', ansons);

                this.forecastData = data;
                this.forecastGridData = cloneDeep(this.forecastData);
                this.getData();
                this.generateSummaryData();
                this.generateSummaryColumns();
            },
            createAppState(dataState) {
                this.take = dataState.take;
                this.skip = dataState.skip;
                this.sort = dataState.sort;

                this.getData();
            },
            customSaveExcel(exportOptions) {
                let saveFn = (dataURL) => saveAs(dataURL, exportOptions.fileName);

                let options = workbookOptions(exportOptions);
                let rows = options.sheets[0].rows;
                rows[3].cells.forEach(cell => cell.color = '#ff0000');

                rows.forEach((row, index) => {
                    if (index >= 2) {
                        row.cells.forEach((cell, index) => {
                            cell.textAlign = 'left';

                            if (index != 0 && index % 3 === 0) {
                                cell.format = '0.0'
                            }

                            if (index % 3 != 0) {
                                cell.format = '$ #,##0'
                            }
                        });

                    }
                })

                let headerOptions = rows[1].cells[0];

                let forecastColumns = cloneDeep(this.forecastColumns);
                forecastColumns.unshift({ field:'displayName', title: 'Player', width: '150px', locked: true, editable: false });

                rows.push({
                    type: 'header',
                    cells: forecastColumns.map((col, index) => {
                        return {
                            ...headerOptions, value: col.title
                        }
                    })
                })

                this.forecastData.forEach(data => {
                    rows.push({
                        type: 'data',
                        cells: forecastColumns.map((col, index) => {
                            let yearString = col.field.split(/(?=[A-Z])/)[0];

                            let cell = {
                                textAlign: 'left',
                                value: data[col.field]
                            }

                            if (data[`${yearString}Projected`]) {
                                cell.color = '#ff0000';
                            }

                            if (index != 0 && index % 3 === 0) {
                                cell.format = '0.0';
                            }

                            if (index % 3 != 0) {
                                cell.format = '$ #,##0';
                            }

                            return cell;
                        })
                    });
                });

                toDataURL(options).then(saveFn);

            },
            dataStateChange(event) {
                this.createAppState(event.data);

                let forecastGrid = this.$refs.forecastgrid.$el.querySelector('.k-grid-content');
                if (forecastGrid) {
                    forecastGrid.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
            },
            async deleteRow(playerId) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.DeleteCommissionForecastData(tokenResponse.accessToken, playerId)
                    .then((res) => {
                        if (res.data.data) {
                            this.assignForecastData(res.data.data);
                            this.showNotification('Data updated successfully');
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })

            },
            exportExcel() {
                let today = new Date().toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: '2-digit'});

                this.customSaveExcel({
                    data: this.summaryData,
                    fileName: `Gold Star Hockey Projected Commissions ${today.split('/').join('-')}`,
                    columns: this.summaryColumns
                })
            },
            generateSummaryColumns() {
                this.summaryColumns = [
                    { cell: 'seasonColumn', field: 'season', title: 'Season', width: '325px', locked: true }
                ];

                let dynamicSummaryColumns = [];

                this.seasonStrings.forEach((seasonString, index) => {
                    dynamicSummaryColumns.push({
                        title: seasonString,
                        width: '460px',
                        children: [
                            { field: `year${index + 1}Salary`, title: 'Salaries', width: '180px', format: '{0:c0}'},
                            { field: `year${index + 1}Commission`, title: 'Commission', width: '180px', format: '{0:c0}' },
                            { field: `year${index + 1}CompPercentage`, title: '%', width: '100px', format: '{0: #.##\\%}' }
                        ]
                    })
                });

                this.summaryColumns.push(...dynamicSummaryColumns);
            },
            generateSummaryData() {
                this.summaryData.forEach(summary => {
                    for (let i = 1; i <= 11; i++) {
                        if (summary.season === 'Guaranteed') {
                            summary[`year${i}Salary`] = this.forecastGridData.reduce((prev, cur) => {
                                if (!cur[`year${i}Projected`]) {
                                    return prev + cur[`year${i}Salary`];
                                }

                                return prev;
                            }, 0);

                            summary[`year${i}Commission`] = this.forecastGridData.reduce((prev, cur) => {
                                if (!cur[`year${i}Projected`]) {
                                    return prev + cur[`year${i}Receivable`];
                                }

                                return prev;
                            }, 0);

                            let compPercentageItems = this.forecastGridData.filter(data => !data[`year${i}Projected`]);

                            if (compPercentageItems.length > 0) {
                                let totalCompPercentage = compPercentageItems.reduce((prev, cur) => {
                                    return prev + cur[`year${i}CompPercentage`];
                                }, 0);

                                summary[`year${i}CompPercentage`] = totalCompPercentage / compPercentageItems.length;
                            } else {
                                summary[`year${i}CompPercentage`] = 0;
                            }                          
                        }

                        if (summary.season === 'Projected') {
                            summary[`year${i}Salary`] = this.forecastGridData.reduce((prev, cur) => {
                                if (cur[`year${i}Projected`]) {
                                    return prev + cur[`year${i}Salary`];
                                }

                                return prev;
                            }, 0);

                            summary[`year${i}Commission`] = this.forecastGridData.reduce((prev, cur) => {
                                if (cur[`year${i}Projected`]) {
                                    return prev + cur[`year${i}Receivable`];
                                }

                                return prev;
                            }, 0);

                            let compPercentageItems = this.forecastGridData.filter(data => data[`year${i}Projected`]);

                            if (compPercentageItems.length > 0) {
                                let totalCompPercentage = compPercentageItems.reduce((prev, cur) => {
                                    return prev + cur[`year${i}CompPercentage`];
                                }, 0);

                                summary[`year${i}CompPercentage`] = totalCompPercentage / compPercentageItems.length;
                            } else {
                                summary[`year${i}CompPercentage`] = 0;
                            }  
                        }

                        if (summary.season === 'Guaranteed + Projected') {
                            summary[`year${i}Salary`] = this.forecastGridData.reduce((prev, cur) => {
                                return prev + cur[`year${i}Salary`];
                            }, 0);

                            summary[`year${i}Commission`] = this.forecastGridData.reduce((prev, cur) => {
                                return prev + cur[`year${i}Receivable`];
                            }, 0);


                            if (this.forecastGridData.length > 0) {
                                let totalCompPercentage = this.forecastGridData.reduce((prev, cur) => {
                                    return prev + cur[`year${i}CompPercentage`];
                                }, 0);

                                summary[`year${i}CompPercentage`] = totalCompPercentage / this.forecastGridData.length;
                            } else {
                                summary[`year${i}CompPercentage`] = 0;
                            }  
                        }
                    }
                });
            },
            getData() {
                let filteredData = this.getFilteredItems();
              
                this.filteredForecastGridData = process(filteredData, {
                    take: this.take,
                    skip: this.skip,
                    group: this.group,
                    sort: this.sort,
                }).data;

                this.total = filteredData.length;
            },
            getFilteredItems() {
                if (this.searchWord) {
                    return this.forecastGridData.filter(data => {
                        return data.firstName.toLowerCase().includes(this.searchWord.toLowerCase()) || data.lastName.toLowerCase().includes(this.searchWord.toLowerCase());
                    });
                } else {
                    return cloneDeep(this.forecastGridData);
                }
            },
            async getForecastItems() {
                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetCommissionForecastItems(tokenResponse.accessToken)
                    .then((res) => {
                        if (res.data.data) {
                            console.log(res.data.data)
                            this.assignForecastData(res.data.data);
                        }
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            async getPlayersToAddToCommissionForecast() {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetPlayersToAddToCommissionForecast(tokenResponse.accessToken) 
                    .then((res) => {
                        if (res.data.data) {
                            this.assignAddRowSelects(res.data.data);
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            getRowStyle(h, trElement, defaultSlots, props) {
                const isProjectedRow = props.dataItem.season === 'Projected';

                const red = {
                    color: 'rgb(255, 0, 0)',
                    backgroundColor: 'rgba(247, 247, 247, 1)'
                };

                const defaultColor = {
                    color: 'rgb(66, 66, 66)'
                };

                const trProps = {
                    style: isProjectedRow ? red : defaultColor
                };

                return h('tr', trProps, defaultSlots);
            },
            getUpdateBtnStatus(item) {
                let originalItem = this.forecastData.find(data => data.dataId === item.dataId);

                if (originalItem) {
                    return isEqual(omit(originalItem, 'inEdit'), omit(item, 'inEdit'));
                }

                return false;
            },
            goToNewPlayer() {
                let test = Math.floor(this.total / 25);
                let skip = test * 25;
                let take = 25;
                this.createAppState({ take: take, skip: skip });

                this.$nextTick(() => {
                    let forecastGrid = this.$refs.forecastgrid.$el.querySelector('.k-grid-content');
                        if (forecastGrid) {
                            forecastGrid.scrollTo({
                                top: forecastGrid.scrollHeight,
                                behavior: 'smooth'
                            });
                        }
                })
            },
            async onAddRow() {
                await this.getPlayersToAddToCommissionForecast();
                this.toggleAddRowDialog();
            },
            onAddRowPlayerFilterChange(evt) {
                this.addRowSelects = filterBy(this.addRowPlayers, evt.filter);
            },
            onAddRowPlayerSelected(evt) {
                if (evt.value.fullName === 'Select Player') return;

                console.log(evt.value);
            },
            onCancelEdit(item) {
                item.inEdit = false;
                let originalItem = this.forecastData.find(data => data.dataId === item.dataId);

                if (originalItem) {
                    let newItemIndex = this.forecastData.findIndex(data => data.dataId === item.dataId);
                    this.forecastGridData[newItemIndex] = cloneDeep(originalItem);
                }

                this.getData();
                this.generateSummaryData();
            },
            onCancelEditAll() {
                this.editAllMode = false;

                this.forecastGridData = cloneDeep(this.forecastData);

                this.getData();
                this.generateSummaryData();
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onCompPercentageUpdated(item, year) {
                if (item[`year${year}CompPercentage`]) {

                    item[`year${year}Receivable`] = item[`year${year}Salary`] * (item[`year${year}CompPercentage`] / 100);
                } else {
                    item[`year${year}Receivable`] = 0;
                }

                if (!item.projected) {
                    item[`year${year}Locked`] = true;
                } else {
                    item[`year${year}Locked`] = false;
                }

                let unfilteredItemIndex = this.forecastGridData.findIndex(data => data.dataId === item.dataId);

                if (unfilteredItemIndex >= 0) {
                    this.forecastGridData[unfilteredItemIndex] = cloneDeep(item);
                }

                this.generateSummaryData();
            },
            onDeleteRow(item) {
                if (item?.playerId) {
                    this.deleteRow(item.playerId); 
                }
            },
            onEditRow(item) {
                item.inEdit = true;

                let unfilteredItem = this.forecastGridData.find(i => i.dataId === item.dataId);
                if (unfilteredItem) {
                    unfilteredItem.inEdit = true;
                }
            },
            onEditAllRows() {
                this.editAllMode = true;

                this.forecastGridData.forEach(item => {
                    item.inEdit = true;
                });

                this.getData();
            },
            onItemChange(e) {
                console.log('item changed')
            },
            onPlayerOrderUpdated(request) {
                this.toggleReorderDialog();

                this.updatePlayers(request);
            },
            onProjectedUpdated(item, year) {
                if (!item.projected) {
                    item[`year${year}Locked`] = true;
                } else {
                    item[`year${year}Locked`] = false;
                }
                
                let unfilteredItemIndex = this.forecastGridData.findIndex(data => data.dataId === item.dataId);

                if (unfilteredItemIndex >= 0) {
                    this.forecastGridData[unfilteredItemIndex] = cloneDeep(item);
                }

                this.generateSummaryData();
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            onRowReorder() {
                this.toggleReorderDialog();
            },
            onSalaryUpdated(item, year) {
                if (item[`year${year}Salary`]) {
                    item[`year${year}Receivable`] = item[`year${year}Salary`] * (item[`year${year}CompPercentage`] / 100);
                } else {
                    item[`year${year}Receivable`] = 0;
                }

                if (!item.projected) {
                    item[`year${year}Locked`] = true;
                } else {
                    item[`year${year}Locked`] = false;
                }

                let unfilteredItemIndex = this.forecastGridData.findIndex(data => data.dataId === item.dataId);

                if (unfilteredItemIndex >= 0) {
                    this.forecastGridData[unfilteredItemIndex] = cloneDeep(item);
                }

                this.generateSummaryData();
            },
            async onSaveAddRowPlayer() {
                let lowestSortOrder = this.forecastGridData.reduce((prev, cur) => {
                    if (!prev || cur.sortOrder > prev.sortOrder) {
                        return cur;
                    } else {
                        return prev;
                    }
                }, null);

                console.log(lowestSortOrder)

                let request = {
                    playerId: this.selectedAddRowPlayer.id,
                    sortOrder: lowestSortOrder ? lowestSortOrder.sortOrder + 1 : 1
                };

                this.toggleAddRowDialog();

                await this.upsertPlayer(request);

                this.goToNewPlayer();
            },
            onSaveAll() {
                let updatedRows = [];

                this.forecastGridData.forEach(data => {
                    let originalItem = this.forecastData.find(item => item.dataId === data.dataId);

                    if (originalItem) {
                        if (!isEqual(omit(originalItem, 'inEdit'), omit(data, 'inEdit'))) {
                            for (const key in data) {
                                if (originalItem[key] != data[key]) {
                                    console.log(key, originalItem[key], data[key])
                                }
                            }

                            updatedRows.push(data);
                        }
                    }
                });

                this.onUpdateRows(updatedRows);
            },
            onSaveContractNote() {
                if (this.selectedNotesRow) {
                    let request = [{
                        playerId: this.selectedNotesRow.playerId,
                        sortOrder: this.selectedNotesRow.sortOrder,
                        contractNotes: this.selectedNotes
                    }]

                    this.toggleContractNotesDialog();
                    this.updatePlayers(request);
                }
            },
            onSearchChange(evt) {
                this.searchWord = evt.value;
                this.getData();
            },
            onUpdateRow(item) {
                this.upsertRow(item);
            },
            onUpdateRows(items) { 
                this.upsertRows(items);
            },
            onUpsertContractNote(item) {
                console.log(item);
                this.selectedNotesRow = item;
                this.selectedNotes = item.contractNotes;

                this.toggleContractNotesDialog();
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            toggleAddRowDialog() {
                this.showAddRowDialog = !this.showAddRowDialog;

                if (this.showAddRowDialog) {
                    this.selectedAddRowPlayer = { id: null, fullName: 'Select Player'};
                }
            },
            toggleContractNotesDialog() {
                this.showContractNotesDialog = !this.showContractNotesDialog;

                if (!this.showContractNotesDialog) {
                    this.selectedNotesRow = null;
                }
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                    this.errorMsg = '';
                }
            },
            toggleReorderDialog() {
                this.showReorderDialog = !this.showReorderDialog;
            },
            async updatePlayers(request) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpdateCommissionForecastPlayers(tokenResponse.accessToken, request)
                    .then((res) => {
                        if (res.data.data) {
                            this.assignForecastData(res.data.data);
                            this.showNotification('Players updated successfully');
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            async upsertPlayer(request) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpsertCommissionForecastPlayer(tokenResponse.accessToken, request)
                    .then((res) => {
                        if (res.data.data) {
                            this.assignForecastData(res.data.data);
                            this.showNotification('Players updated successfully');
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            async upsertRow(request) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpsertCommissionForecastData(tokenResponse.accessToken, request)
                    .then((res) => {
                        if (res.data.data) {
                            this.assignForecastData(res.data.data);
                            this.showNotification('Data updated successfully');
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            async upsertRows(request) {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpsertCommissionForecastDatas(tokenResponse.accessToken, request)
                    .then((res) => {
                        if (res.data.data) {
                            this.assignForecastData(res.data.data);
                            this.showNotification('Data updated successfully');
                            this.editAllMode = false;
                        }

                        this.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            }
        },
        filters: {
            formatCurrency(val) {
                if (val === null || val === undefined || val === '') return '';

                if (val.toString().includes('$')) return val;

                if (isNaN(val)) {
                    val = 0;
                }

                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                });

                return formatter.format(val);
            },
            formatCurrencyDec(val) {
                if (val === null || val === undefined || val === '') return '';

                if (val.toString().includes('$')) return val;

                if (isNaN(val)) {
                    val = 0;
                }

                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });

                return formatter.format(val);
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-loader': Loader,
            'k-grid': Grid,
            'k-checkbox': Checkbox,
            'k-button': Button,
            'k-numeric-input': NumericTextBox,
            'view-header': viewHeader,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-input': Input,
            'k-dropdown': DropDownList,
            'k-action-bar': DialogActionsBar,
            'commissions-forecast-reorder-dialog': commissionsForecastReorderDialog
        }
    }
</script>

<style>
    @import '/src/assets/css/commissionsForecastComponent.css';
</style>